import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import SignaturePad from "signature_pad";
import { FileSignature, Save, Download, Trash2, PenLine, Plus, Calendar as CalendarIcon, IndianRupee } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { buildBookingSlipPdf } from "@/pages/shoots/shootHelpers";

/** Studio Booking Slip — digitised paper slip. Signature is drawn on a
 *  DPR-aware canvas (mouse + touch via signature_pad). Edit mode preserves
 *  the existing signature when the user doesn't re-draw. */
export default function StudioBookingSlip() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const canView = !!user && (user.is_super_admin || user.is_admin || user.team === "Shoot Team");

  const canvasRef = useRef(null);
  const padRef = useRef(null);
  const [form, setForm] = useState({
    date: "", customer_name: "", start_time: "", end_time: "",
    advance: "", pending: "", notes: "", signature_data_url: "",
  });
  const [existingSig, setExistingSig] = useState("");   // signature already on record
  const [saving, setSaving] = useState(false);

  // Saved-slips list rendered as a mini archive above the form. Read-only for
  // everyone with `require_shoot` access; edit/delete gated on the same rules
  // as create (super-admin, admin, or Shoot Team members).
  const [slips, setSlips] = useState([]);
  const [slipsLoading, setSlipsLoading] = useState(false);
  const [pdfBusyId, setPdfBusyId] = useState("");

  const loadSlips = useCallback(async () => {
    if (!canView) return;
    setSlipsLoading(true);
    try {
      const { data } = await api.get("/shoots/bookings/slips");
      setSlips(Array.isArray(data) ? data : []);
    } catch (_e) {
      // Silent — the primary form still works even if the archive fails.
    } finally {
      setSlipsLoading(false);
    }
  }, [canView]);
  useEffect(() => { loadSlips(); }, [loadSlips]);

  // ---- Init signature pad on mount + DPR resize handling ----
  useEffect(() => {
    if (!canView || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      const ctx = canvas.getContext("2d");
      ctx.scale(dpr, dpr);
      // Clear (SignaturePad will redraw on user input)
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      if (padRef.current) padRef.current.clear();
    };
    resize();
    const pad = new SignaturePad(canvas, { penColor: "#0a0a0c", backgroundColor: "rgba(0,0,0,0)" });
    padRef.current = pad;
    window.addEventListener("resize", resize);
    return () => { window.removeEventListener("resize", resize); pad.off(); };
  }, [canView]);

  // ---- Load existing slip for edit ----
  useEffect(() => {
    if (!canView || !id) return;
    api.get(`/shoots/bookings/slips/${id}`).then(({ data }) => {
      setForm({
        date: data.date || "",
        customer_name: data.customer_name || "",
        start_time: data.start_time || "",
        end_time: data.end_time || "",
        advance: String(data.advance ?? ""),
        pending: String(data.pending ?? ""),
        notes: data.notes || "",
        signature_data_url: data.signature_data_url || "",
      });
      setExistingSig(data.signature_data_url || "");
    }).catch(() => toast.error("Failed to load slip"));
  }, [canView, id]);

  const clearSig = () => {
    padRef.current?.clear();
    setExistingSig("");   // deliberately dropping the on-record signature too
    setForm((f) => ({ ...f, signature_data_url: "" }));
  };

  const captureSig = () => {
    if (!padRef.current || padRef.current.isEmpty()) return existingSig || "";
    return padRef.current.toDataURL("image/png");
  };

  const buildPayload = () => ({
    date: form.date,
    customer_name: form.customer_name.trim(),
    start_time: form.start_time,
    end_time: form.end_time,
    advance: parseFloat(form.advance) || 0,
    pending: parseFloat(form.pending) || 0,
    notes: form.notes.trim(),
    // Preserve the on-record signature when the user hasn't drawn a new one —
    // backend has the matching "skip empty sig on PATCH" guard so this is safe.
    signature_data_url: captureSig(),
  });

  const save = async () => {
    if (!form.date || !form.customer_name.trim()) {
      toast.error("Date + customer name are required");
      return null;
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      const res = id
        ? await api.patch(`/shoots/bookings/slips/${id}`, payload)
        : await api.post("/shoots/bookings/slips", payload);
      toast.success(id ? "Slip updated" : "Slip saved");
      if (!id) nav(`/shoots/booking-slip/${res.data.id}`, { replace: true });
      loadSlips();
      return res.data;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to save");
      return null;
    } finally { setSaving(false); }
  };

  const saveAndDownload = async () => {
    const saved = await save();
    if (saved) { buildBookingSlipPdf(saved); loadSlips(); }
  };

  // Delete a slip from the archive. Only allowed for slip creators + admins;
  // the backend returns 403 for editors, and we surface that toast verbatim.
  const deleteSlip = async (slip) => {
    if (!window.confirm(`Delete slip for ${slip.customer_name || "Unknown"} on ${slip.date}? This can't be undone.`)) return;
    try {
      await api.delete(`/shoots/bookings/slips/${slip.id}`);
      toast.success("Slip deleted");
      // If the user was editing the slip they just deleted, kick them back to a blank form.
      if (id === slip.id) nav("/shoots/booking-slip", { replace: true });
      loadSlips();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to delete slip");
    }
  };

  // Regenerate the PDF for a saved slip without opening its edit form.
  const downloadSlipPdf = async (slip) => {
    setPdfBusyId(slip.id);
    try {
      const { data } = await api.get(`/shoots/bookings/slips/${slip.id}`);
      buildBookingSlipPdf(data);
    } catch (_e) {
      toast.error("Failed to load slip for PDF");
    } finally { setPdfBusyId(""); }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      const [y, m, d] = iso.split("-");
      return `${d}/${m}/${y}`;
    } catch { return iso; }
  };

  if (!canView) return <div className="p-6 text-white/60">Shoot Team access only.</div>;

  // Duration preview
  const dur = (() => {
    if (!form.start_time || !form.end_time) return 0;
    const [sh, sm] = form.start_time.split(":").map(Number);
    const [eh, em] = form.end_time.split(":").map(Number);
    let mins = (eh * 60 + em) - (sh * 60 + sm);
    if (mins <= 0) mins += 24 * 60;
    return mins;
  })();

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-5" data-testid="booking-slip-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[0.7rem] uppercase tracking-[0.3em] text-white/40 flex items-center gap-2"><FileSignature size={12} /> Studio</div>
          <h1 className="text-3xl sm:text-4xl font-display tracking-tight">Studio Booking Slip</h1>
          <p className="text-sm text-white/50 mt-1">Digital version of the paper slip — collect signature on device and email a PDF copy.</p>
        </div>
        {id && (
          <Button
            variant="outline"
            className="border-white/15 hover:border-red-500/40 rounded-md"
            data-testid="slip-new-btn"
            onClick={() => nav("/shoots/booking-slip")}
          >
            <Plus size={13} className="mr-1.5" /> New slip
          </Button>
        )}
      </div>

      {/* Two-column layout on desktop: form on the left, saved-slips archive card
          on the right (sticks to the top so it stays in view while filling long
          forms). On mobile the archive drops below the form so the primary
          action stays above the fold. */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-start">
      {/* Saved slips archive — collapses to nothing when empty. Sorted newest-first
          by the backend (date DESC). Click any row to open + edit / re-generate PDF. */}
      <div className="rounded-lg border border-white/10 bg-black/25 overflow-hidden order-2 lg:order-2 lg:col-span-1 lg:sticky lg:top-4" data-testid="slip-archive-panel">
        <div className="px-4 py-2.5 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CalendarIcon size={13} className="text-white/50" />
            <div className="text-xs uppercase tracking-widest text-white/60 font-mono">Saved slips</div>
          </div>
          <div className="text-[0.7rem] text-white/40 font-mono" data-testid="slip-archive-count">
            {slipsLoading ? "loading…" : `${slips.length} on record`}
          </div>
        </div>
        {slips.length === 0 && !slipsLoading ? (
          <div className="px-4 py-6 text-center text-xs text-white/40" data-testid="slip-archive-empty">
            No slips saved yet — fill the form below and hit Save.
          </div>
        ) : (
          <ul className="divide-y divide-white/5 max-h-64 overflow-y-auto" data-testid="slip-archive-list">
            {slips.map((s) => {
              const isActive = s.id === id;
              return (
                <li
                  key={s.id}
                  data-testid={`slip-archive-row-${s.id}`}
                  className={`px-4 py-2.5 flex items-center gap-3 hover:bg-white/[0.03] transition ${isActive ? "bg-red-500/[0.06]" : ""}`}
                >
                  <button
                    type="button"
                    onClick={() => nav(`/shoots/booking-slip/${s.id}`)}
                    className="flex-1 min-w-0 text-left"
                    data-testid={`slip-archive-open-${s.id}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-mono text-xs text-white/50 shrink-0">{fmtDate(s.date)}</span>
                      <span className="text-sm text-white truncate">{s.customer_name || "Unknown"}</span>
                      {isActive && <span className="text-[0.65rem] uppercase tracking-widest text-red-300 font-mono shrink-0">Editing</span>}
                    </div>
                    <div className="flex items-center gap-3 mt-0.5 text-[0.7rem] text-white/40 font-mono">
                      {s.start_time && s.end_time && <span>{s.start_time}–{s.end_time}</span>}
                      {typeof s.advance === "number" && s.advance > 0 && (
                        <span className="text-emerald-300/80 inline-flex items-center"><IndianRupee size={9} className="mr-0.5" />{s.advance.toLocaleString("en-IN")} adv</span>
                      )}
                      {typeof s.pending === "number" && s.pending > 0 && (
                        <span className="text-amber-300/80 inline-flex items-center"><IndianRupee size={9} className="mr-0.5" />{s.pending.toLocaleString("en-IN")} pending</span>
                      )}
                      {s.signature_data_url && <span className="text-white/50">signed</span>}
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => downloadSlipPdf(s)}
                    disabled={pdfBusyId === s.id}
                    className="text-white/50 hover:text-white p-1.5 rounded shrink-0 disabled:opacity-40"
                    title="Download PDF"
                    data-testid={`slip-archive-pdf-${s.id}`}
                  >
                    <Download size={13} />
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteSlip(s)}
                    className="text-white/40 hover:text-red-400 p-1.5 rounded shrink-0"
                    title="Delete slip"
                    data-testid={`slip-archive-delete-${s.id}`}
                  >
                    <Trash2 size={13} />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="rounded-lg border border-white/10 bg-black/25 p-4 space-y-4 order-1 lg:order-1 lg:col-span-2">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="overline">Date <span className="text-[#E11D2E]">*</span></Label>
            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="slip-date" className="bg-black/40 border-white/10 mt-1.5 [color-scheme:dark]" />
          </div>
          <div>
            <Label className="overline">Name of person / company <span className="text-[#E11D2E]">*</span></Label>
            <Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="slip-name" className="bg-black/40 border-white/10 mt-1.5" placeholder="Customer name" />
          </div>
          <div>
            <Label className="overline">Start time</Label>
            <Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="slip-start" className="bg-black/40 border-white/10 mt-1.5 [color-scheme:dark]" />
          </div>
          <div>
            <Label className="overline">End time</Label>
            <Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} data-testid="slip-end" className="bg-black/40 border-white/10 mt-1.5 [color-scheme:dark]" />
          </div>
        </div>
        {dur > 0 && (
          <div className="text-xs text-white/50 font-mono">Duration: {(dur / 60).toFixed(2)} h · {dur} min{dur > 60 * 24 - (parseInt(form.start_time.split(":")[0], 10) * 60 + parseInt(form.start_time.split(":")[1], 10) || 0) ? " (overnight)" : ""}</div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="overline">Advance paid (₹)</Label>
            <Input type="number" min="0" step="0.01" value={form.advance} onChange={(e) => setForm({ ...form, advance: e.target.value })} data-testid="slip-advance" className="bg-black/40 border-white/10 mt-1.5" />
          </div>
          <div>
            <Label className="overline">Pending balance (₹)</Label>
            <Input type="number" min="0" step="0.01" value={form.pending} onChange={(e) => setForm({ ...form, pending: e.target.value })} data-testid="slip-pending" className="bg-black/40 border-white/10 mt-1.5" />
          </div>
        </div>

        <div>
          <Label className="overline">Additional equipment / notes</Label>
          <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="slip-notes" className="bg-black/40 border-white/10 mt-1.5 min-h-[80px]" placeholder="Extra gear rented, deposit terms, etc." />
        </div>

        {/* Signature */}
        <div>
          <Label className="overline flex items-center gap-2"><PenLine size={12} /> Customer signature</Label>
          <div className="relative mt-1.5 rounded-md border border-dashed border-white/20 bg-white h-40 overflow-hidden" data-testid="slip-signature-wrap">
            <canvas ref={canvasRef} className="w-full h-full touch-none block" />
            {existingSig && (
              <div className="absolute inset-0 pointer-events-none flex items-center justify-center bg-white">
                <img src={existingSig} alt="signature" className="max-h-32" />
              </div>
            )}
          </div>
          <div className="flex items-center justify-between mt-2">
            <div className="text-[0.7rem] text-white/40">Sign here — mouse or finger. Existing signature is preserved on save unless you clear.</div>
            <Button variant="ghost" onClick={clearSig} size="sm" data-testid="slip-signature-clear" className="text-white/60"><Trash2 size={12} className="mr-1" /> Clear</Button>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch gap-2 pt-2 border-t border-white/5">
          <Button onClick={save} disabled={saving} className="bg-[#E11D2E] hover:bg-[#F03041] rounded-md flex-1" data-testid="slip-save-btn">
            <Save size={13} className="mr-1.5" /> {saving ? "Saving…" : id ? "Update slip" : "Save slip"}
          </Button>
          <Button onClick={saveAndDownload} disabled={saving} variant="outline" className="border-white/15 hover:border-red-500/40 flex-1" data-testid="slip-pdf-btn">
            <Download size={13} className="mr-1.5" /> Save + Download PDF
          </Button>
        </div>
      </div>
      </div>
    </div>
  );
}
